import React, { useState } from 'react';
import Card from './Card';
import Button from './Button';
import StepIndicator from './StepIndicator';
import LoadingSpinner from './LoadingSpinner';
import Alert from './Alert';
import DuplicateWarning from './DuplicateWarning';
import ThankYouPage from './ThankYouPage';
import FAQ from './FAQ';
import PersonalDataStep from './steps/PersonalDataStep';
import ProfessionalInfoStep from './steps/ProfessionalInfoStep';
import SummaryStep from './steps/SummaryStep';
import { saveToSupabase } from '../services/supabaseService';
import { saveToGoogleSheets } from '../services/googleSheetsService';

const Wizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Dados pessoais
    nome: '',
    telefone: '',
    email: '',
    cidade: '',
    estado: '',
    bairro: '',
    
    // Informações profissionais
    profissoes: [],
    habilidades: '',
    pretensao: '',
    experiencia: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const totalSteps = 3;

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo quando ele for atualizado
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.nome.trim()) newErrors.nome = 'Nome é obrigatório';
      
      // Validação do telefone - deve ter pelo menos DDD + 8 dígitos
      const phoneNumbers = formData.telefone.replace(/\D/g, '');
      if (!formData.telefone.trim()) {
        newErrors.telefone = 'Telefone é obrigatório';
      } else if (phoneNumbers.length < 10) {
        newErrors.telefone = 'Telefone deve ter DDD + 8 dígitos (ex: (11) 99999-9999)';
      }

      // Validação dos campos de endereço
      if (!formData.cidade.trim()) newErrors.cidade = 'Cidade é obrigatória';
      if (!formData.estado.trim()) newErrors.estado = 'Estado é obrigatório';
      if (!formData.bairro.trim()) newErrors.bairro = 'Bairro é obrigatório';
    }

    if (step === 2) {
      if (!formData.habilidades.trim()) {
        newErrors.habilidades = 'Habilidades específicas são obrigatórias';
      } else if (formData.habilidades.trim().length < 20) {
        newErrors.habilidades = 'Por favor, seja mais específico. Descreva detalhadamente suas habilidades (mínimo 20 caracteres)';
      } else {
        // Verificar se contém palavras muito genéricas
        const palavrasGenericas = ['trabalho', 'geral', 'manutenção', 'reparo', 'serviço', 'obra', 'conserto'];
        const habilidadesLower = formData.habilidades.toLowerCase();
        const contemGenericas = palavrasGenericas.some(palavra => 
          habilidadesLower.includes(palavra) && formData.habilidades.trim().length < 50
        );
        
        if (contemGenericas) {
          newErrors.habilidades = 'Seja mais específico! Exemplos: "instalação de chuveiros elétricos", "assentamento de azulejos", "pintura de paredes externas". Evite termos genéricos.';
        }
      }
      
      if (!formData.experiencia.trim()) newErrors.experiencia = 'Experiência profissional é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (validateStep(currentStep)) {
      setIsLoading(true);
      setAlertMessage(null);
      setDuplicateInfo(null);
      
      try {
        // Salvar no Supabase
        const result = await saveToSupabase(formData);
        
        if (result.success) {
          // Se salvou com sucesso no Supabase, tenta salvar no Google Sheets
          const sheetsResult = await saveToGoogleSheets(formData);
          
          if (!sheetsResult.success && !sheetsResult.skipped) {
            console.warn('Aviso: Dados salvos no Supabase, mas houve erro ao salvar no Google Sheets:', sheetsResult.message);
            // Ainda mostra sucesso pois o Supabase é o principal
          }
          
          setShowThankYou(true);
        } else if (result.isDuplicate) {
          setDuplicateInfo({
            existingData: result.existingData,
            duplicateField: result.duplicateField,
            message: result.message
          });
        } else {
          setAlertMessage({
            type: 'error',
            message: result.message
          });
        }
      } catch (error) {
        setAlertMessage({
          type: 'error',
          message: 'Erro inesperado. Tente novamente.'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDuplicateContinue = async () => {
    setIsLoading(true);
    setDuplicateInfo(null);
    
    try {
      // Força o salvamento mesmo com duplicidade
      const result = await saveToSupabase({ ...formData, forceSave: true });
      
      if (result.success) {
        // Se salvou com sucesso no Supabase, tenta salvar no Google Sheets
        const sheetsResult = await saveToGoogleSheets(formData);
        
        if (!sheetsResult.success && !sheetsResult.skipped) {
          console.warn('Aviso: Dados salvos no Supabase, mas houve erro ao salvar no Google Sheets:', sheetsResult.message);
          // Ainda mostra sucesso pois o Supabase é o principal
        }
        
        setShowThankYou(true);
      } else {
        setAlertMessage({
          type: 'error',
          message: result.message
        });
      }
    } catch (error) {
      setAlertMessage({
        type: 'error',
        message: 'Erro inesperado. Tente novamente.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicateCancel = () => {
    setDuplicateInfo(null);
    setCurrentStep(1); // Volta para a primeira etapa
  };

  const handleStartNew = () => {
    setShowThankYou(false);
    setShowForm(false);
    setCurrentStep(1);
    setFormData({
      nome: '',
      telefone: '',
      email: '',
      cidade: '',
      estado: '',
      bairro: '',
      profissoes: [],
      habilidades: '',
      pretensao: '',
      experiencia: ''
    });
    setErrors({});
    setAlertMessage(null);
    setDuplicateInfo(null);
  };

  const handleStartRegistration = () => {
    setShowForm(true);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalDataStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <ProfessionalInfoStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 3:
        return (
          <SummaryStep formData={formData} />
        );
      default:
        return null;
    }
  };

  // Se está mostrando a página de agradecimento
  if (showThankYou) {
    return <ThankYouPage onStartNew={handleStartNew} />;
  }

  // Se não está mostrando o formulário, mostra as explicações iniciais
  if (!showForm) {
    return (
      <div className="min-h-screen bg-gray-50 py-4">
        <div className="max-w-md sm:max-w-2xl lg:max-w-4xl mx-auto px-4">
          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
              Cadastro de Profissionais
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-4">
              Junte-se à nossa rede de profissionais de manutenção residencial
            </p>
          </div>

          {/* FAQ - Responsivo */}
          <div className="mb-6">
            <FAQ />
          </div>

          {/* Botão para iniciar cadastro */}
          <div className="text-center">
            <Button
              onClick={handleStartRegistration}
              className="w-full sm:w-auto sm:px-8 py-3 text-lg font-medium"
            >
              Realizar Cadastro
            </Button>
            <p className="text-sm sm:text-base text-gray-500 mt-2">
              Clique para começar seu cadastro como profissional
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-md sm:max-w-2xl lg:max-w-4xl mx-auto px-4">
        <div className="text-center mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Cadastro de Profissionais
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            Preencha as informações para se cadastrar como profissional de manutenção residencial
          </p>
        </div>

        {/* Formulário Principal */}
        <div>
          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />

          <Card className="mt-4">
          {/* Alertas */}
          {alertMessage && (
            <div className="mb-6">
              <Alert 
                type={alertMessage.type} 
                message={alertMessage.message}
                onClose={() => setAlertMessage(null)}
              />
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <LoadingSpinner message="Verificando dados e salvando cadastro..." />
          )}

          {/* Aviso de duplicidade */}
          {duplicateInfo && !isLoading && (
            <DuplicateWarning
              duplicateInfo={duplicateInfo}
              onContinue={handleDuplicateContinue}
              onCancel={handleDuplicateCancel}
            />
          )}

          {/* Formulário normal */}
          {!isLoading && !duplicateInfo && renderStep()}
          
          {/* Botões de navegação */}
          {!isLoading && !duplicateInfo && (
            <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={currentStep === 1 ? () => setShowForm(false) : prevStep}
                className="text-sm py-2 px-4"
              >
                {currentStep === 1 ? 'Voltar' : 'Anterior'}
              </Button>
              
              {currentStep < totalSteps ? (
                <Button onClick={nextStep} className="text-sm py-2 px-4">
                  Próximo
                </Button>
              ) : (
                <Button onClick={handleSubmit} className="text-sm py-2 px-4">
                  Concluir Cadastro
                </Button>
              )}
            </div>
          )}
        </Card>
        </div>
      </div>
    </div>
  );
};

export default Wizard;
